/** Cancels work across identity transitions, including A -> B -> A switches. */
export class SessionChangedError extends Error {
  constructor() {
    super('The active session changed.')
    this.name = 'SessionChangedError'
  }
}

export function createSessionScope() {
  let identity: string | null = null
  let generation = 0
  let controller = new AbortController()
  const listeners = new Set<() => void>()
  return {
    transition(nextIdentity: string | null) {
      if (nextIdentity === identity) return
      controller.abort(new SessionChangedError())
      controller = new AbortController()
      identity = nextIdentity
      generation++
      listeners.forEach(listener => listener())
    },
    subscribe(listener: () => void) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    capture() {
      const capturedGeneration = generation
      return {
        identity,
        generation,
        signal: controller.signal,
        assertCurrent() {
          if (capturedGeneration !== generation) throw new SessionChangedError()
        },
      }
    },
  }
}

export type SessionSnapshot = ReturnType<
  ReturnType<typeof createSessionScope>['capture']
>

/** Read asynchronously, then apply synchronously only while the owner is current. */
export async function restoreInScope<T>(options: {
  read(): PromiseLike<T> | T
  isCurrent(): boolean
  apply(value: T): void
}): Promise<boolean> {
  const value = await options.read()
  if (!options.isCurrent()) return false
  options.apply(value)
  return true
}

/** Refresh results and cleanup always target the account captured before I/O. */
export function createAccountRefresh<T>(options: {
  refresh(accountId: string, session: SessionSnapshot): Promise<T>
  save(accountId: string, tokens: T): Promise<void>
  remove(accountId: string): Promise<void>
  shouldRemove(error: unknown): boolean
}) {
  const pending = new Map<string, Promise<T>>()
  return (accountId: string, session: SessionSnapshot): Promise<T> => {
    session.assertCurrent()
    const key = `${accountId}:${session.generation}`
    const existing = pending.get(key)
    if (existing) return existing
    const promise = Promise.resolve()
      .then(() => options.refresh(accountId, session))
      .then(async tokens => {
        session.assertCurrent()
        await options.save(accountId, tokens)
        session.assertCurrent()
        return tokens
      })
      .catch(async error => {
        session.assertCurrent()
        if (options.shouldRemove(error)) await options.remove(accountId)
        throw error
      })
      .finally(() => {
        if (pending.get(key) === promise) pending.delete(key)
      })
    pending.set(key, promise)
    return promise
  }
}

/** Works in older WebViews without AbortSignal.any; dispose after request settles. */
export function linkAbortSignals(
  signals: Array<AbortSignal | null | undefined>,
) {
  const controller = new AbortController()
  const bindings = signals
    .filter((signal): signal is AbortSignal => !!signal)
    .map(signal => {
      const abort = () => controller.abort(signal.reason)
      if (signal.aborted) abort()
      else signal.addEventListener('abort', abort, { once: true })
      return { signal, abort }
    })
  return {
    signal: controller.signal,
    dispose() {
      bindings.forEach(({ signal, abort }) =>
        signal.removeEventListener('abort', abort),
      )
    },
  }
}
